import React, { useCallback, useMemo } from 'react';
import { LogIn } from 'lucide-react';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { Column } from 'components/display/Table/types';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { useTableFiltering } from 'shared/contexts/table-context/hooks/useTableFiltering';
import { useTableSearch } from 'shared/contexts/table-context/hooks/useTableSearch';
import { FacetPeriod } from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { connexionsAPI } from 'services/swagger-api';
import { ConnexionListItem } from 'api/courtiers';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { formatInstant, toDayInput } from 'core/utils/dates';

/** Libellés des actions du journal de sécurité ABP (valeur brute si inconnue). */
export const CONNEXION_ACTION_LABELS: Record<string, string> = {
  LoginSucceeded: 'Connexion réussie',
  LoginFailed: 'Échec de connexion',
  ImpersonateUser: 'Impersonation',
  ChangePassword: 'Changement de mot de passe'
};

const actionLabel = (action?: string | null): string =>
  action ? CONNEXION_ACTION_LABELS[action] ?? action : '—';

const inputClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none';

/** Période par défaut : les 30 derniers jours (dates calendaires du navigateur). */
const defaultPeriod = (): FacetPeriod => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 30);
  return { startDate: toDayInput(start), endDate: toDayInput(today) };
};

/**
 * Barre de filtres (utilisateur = searchString, action, période), branchée sur le state
 * du tableau. Remplace la barre native : son bouton « Filtrer » n'ouvre rien sans facettes.
 */
const ConnexionsFilters: React.FC = () => {
  const { searchString, setSearchString } = useTableSearch();
  const { filters, setFilter, removeFilter } = useTableFiltering();
  const period: FacetPeriod = filters.period ?? {};

  const setPeriod = (key: keyof FacetPeriod, value: string) =>
    setFilter('period', { ...period, [key]: value || undefined });

  return (
    <div className="mb-4 flex flex-wrap items-end gap-4">
      <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
        Utilisateur
        <input
          type="text"
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
          placeholder="Rechercher un utilisateur..."
          className={inputClass}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
        Action
        <select
          value={filters.action ?? ''}
          onChange={(e) =>
            e.target.value ? setFilter('action', e.target.value) : removeFilter('action')
          }
          className={inputClass}
        >
          <option value="">Toutes les actions</option>
          {Object.entries(CONNEXION_ACTION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
        Du
        <input
          type="date"
          value={period.startDate ?? ''}
          onChange={(e) => setPeriod('startDate', e.target.value)}
          className={inputClass}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
        Au
        <input
          type="date"
          value={period.endDate ?? ''}
          onChange={(e) => setPeriod('endDate', e.target.value)}
          className={inputClass}
        />
      </label>
    </div>
  );
};

/** Filtres du tableau → filtres de la commande, partagés par la liste et l'export CSV. */
const mapConnexionFilters = (filters?: Record<string, any>) => {
  const period: FacetPeriod | undefined = filters?.period;
  return {
    action: filters?.action || undefined,
    period:
      period?.startDate || period?.endDate
        ? { startDate: period.startDate || undefined, endDate: period.endDate || undefined }
        : undefined
  };
};

export const ConnexionsView: React.FC = () => {
  const columns: Column<ConnexionListItem>[] = useMemo(
    () => [
      {
        key: 'date',
        label: 'Date',
        render: (value) => (
          <span className="text-sm text-gray-700">{formatInstant(value as Date)}</span>
        )
      },
      {
        key: 'userName',
        label: 'Utilisateur',
        render: (value) => (
          <span className="font-semibold text-gray-900">{(value as string) || '—'}</span>
        )
      },
      {
        key: 'action',
        label: 'Action',
        render: (value) => (
          <span className="text-sm text-gray-700">{actionLabel(value as string)}</span>
        )
      },
      {
        key: 'clientIpAddress',
        label: 'Adresse IP',
        render: (value) => (
          <span className="font-mono text-xs text-gray-600">{(value as string) || '—'}</span>
        )
      },
      {
        key: 'browserInfo',
        label: 'Navigateur',
        render: (value) => (
          <span className="text-xs text-gray-600">{(value as string) || '—'}</span>
        )
      }
    ],
    []
  );

  // Calculée une fois : point de départ et cible du « Réinitialiser »
  const initialFilters = useMemo(() => ({ period: defaultPeriod() }), []);

  const fetchConnexions = useCallback(
    async (params: any): Promise<TableResponse<ConnexionListItem>> => {
      const result = await connexionsAPI.getAdminConnexions({
        getConnexionListCommand: {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          filters: mapConnexionFilters(params.filters)
        }
      });
      const items = result.items || [];
      return {
        items,
        totalItems: result.totalCount || items.length,
        totalPages: Math.ceil((result.totalCount || items.length) / params.pageSize),
        currentPage: params.pageNumber
      };
    },
    []
  );

  return (
    <SimpleDashboardContent>
      <GenericViewHeader title="Connexions" icon={LogIn} />
      <ServerTable<ConnexionListItem>
        apiConfig={{ fetchData: fetchConnexions }}
        columns={columns}
        initialFilters={initialFilters}
        enableFilters={false}
        renderCustomFilters={() => <ConnexionsFilters />}
        columnCustomization={{ storageKey: 'bo-connexions' }}
        onExport={(p) =>
          exportListCsv(
            '/api/backoffice/admin/connexions/liste/csv',
            buildExportCommand(
              { ...p, searchString: p.searchString || undefined },
              mapConnexionFilters(p.filters)
            )
          )
        }
      />
    </SimpleDashboardContent>
  );
};

export default ConnexionsView;
